import { useEffect, useRef, useState } from "react";
import {
  getDocument,
  GlobalWorkerOptions,
  PDFDocumentLoadingTask,
  PDFDocumentProxy,
} from "pdfjs-dist";
import {
  DEFAULT_OCTET_STREAM,
  getContentTypeFromExtension,
  getExtension,
} from "utility/mimeTypes";

GlobalWorkerOptions.workerSrc = `${import.meta.env.BASE_URL}pdf.worker.min.mjs`;

export type PreviewKind = "pdf" | "image" | "unsupported";

export interface PreviewState {
  contentType: string;
  fileName: string;
  kind: PreviewKind;
  url: string;
}

export interface FetchBlobResult {
  blob: Blob;
  contentType?: string;
}

interface UseBlobPreviewOptions {
  /** Set to true to trigger a fetch; false resets state and skips fetching. */
  enabled: boolean;
  /** Called when a fetch is needed. Should be stable (memoized with useCallback). */
  fetchBlob: () => Promise<FetchBlobResult>;
  /** File name used for display in the preview. */
  fileName: string;
  /** Optional MIME type hint from the record (e.g. from server metadata). */
  contentTypeHint?: string | null;
  /** Optional file name hint for extension-based MIME detection. */
  nameHint?: string | null;
  /** Optional file path hint for extension-based MIME detection. */
  pathHint?: string | null;
}

interface UseBlobPreviewResult {
  hasLoadError: boolean;
  isLoading: boolean;
  pageCount: number;
  pdfDocument: PDFDocumentProxy | null;
  previewState: PreviewState | null;
}

const resolveContentType = (
  responseContentType: string | undefined,
  blobType: string | undefined,
  contentTypeHint: string | null | undefined,
  nameHint: string | null | undefined,
  pathHint: string | null | undefined,
): string => {
  const candidates = [
    responseContentType,
    blobType,
    contentTypeHint ?? undefined,
  ].filter((ct): ct is string => !!ct && ct !== DEFAULT_OCTET_STREAM);

  if (candidates.length > 0) {
    return candidates[0];
  }

  return (
    getContentTypeFromExtension(getExtension(nameHint)) ||
    getContentTypeFromExtension(getExtension(pathHint)) ||
    DEFAULT_OCTET_STREAM
  );
};

const getPreviewKind = (contentType: string): PreviewKind => {
  if (contentType.includes("pdf")) {
    return "pdf";
  }
  if (contentType.startsWith("image/")) {
    return "image";
  }
  return "unsupported";
};

export const useBlobPreview = ({
  enabled,
  fetchBlob,
  fileName,
  contentTypeHint,
  nameHint,
  pathHint,
}: UseBlobPreviewOptions): UseBlobPreviewResult => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadError, setHasLoadError] = useState(false);
  const [previewState, setPreviewState] = useState<PreviewState | null>(null);
  const [pdfDocument, setPdfDocument] = useState<PDFDocumentProxy | null>(null);
  const [pageCount, setPageCount] = useState(0);

  // Keep a ref so the effect always calls the latest fetchBlob without
  // re-running on every render (avoids re-fetching on auth token refresh).
  const fetchBlobRef = useRef(fetchBlob);
  useEffect(() => {
    fetchBlobRef.current = fetchBlob;
  }, [fetchBlob]);

  useEffect(() => {
    if (!enabled) {
      setPreviewState(null);
      setPdfDocument(null);
      setPageCount(0);
      setHasLoadError(false);
      setIsLoading(false);
      return;
    }

    let objectUrl: string | null = null;
    let pdfLoadingTask: PDFDocumentLoadingTask | null = null;
    let loadedPdfDoc: PDFDocumentProxy | null = null;
    let isCancelled = false;

    const loadPreview = async () => {
      setIsLoading(true);
      setHasLoadError(false);
      setPdfDocument(null);
      setPageCount(0);

      try {
        const { blob, contentType: responseContentType } =
          await fetchBlobRef.current();

        const contentType = resolveContentType(
          responseContentType,
          blob.type,
          contentTypeHint,
          nameHint,
          pathHint,
        );

        objectUrl = window.URL.createObjectURL(blob);

        const nextPreviewState: PreviewState = {
          contentType,
          fileName,
          kind: getPreviewKind(contentType),
          url: objectUrl,
        };

        if (nextPreviewState.kind === "pdf") {
          const fileData = new Uint8Array(await blob.arrayBuffer());
          pdfLoadingTask = getDocument({ data: fileData });
          const loadedPdf = await pdfLoadingTask.promise;

          if (isCancelled) {
            await loadedPdf.destroy();
            return;
          }

          loadedPdfDoc = loadedPdf;
          setPdfDocument(loadedPdf);
          setPageCount(loadedPdf.numPages);
        }

        if (!isCancelled) {
          setPreviewState(nextPreviewState);
        }
      } catch {
        if (!isCancelled) {
          setPreviewState(null);
          setPdfDocument(null);
          setPageCount(0);
          setHasLoadError(true);
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false);
        }
      }
    };

    loadPreview();

    return () => {
      isCancelled = true;
      if (pdfLoadingTask) {
        void pdfLoadingTask.destroy();
      }
      if (loadedPdfDoc) {
        setPdfDocument(null);
        setPreviewState(null);
        void loadedPdfDoc.destroy();
      }
      if (objectUrl) {
        window.URL.revokeObjectURL(objectUrl);
      }
    };
  }, [enabled, contentTypeHint, fileName, nameHint, pathHint]);

  return {
    hasLoadError,
    isLoading,
    pageCount,
    pdfDocument,
    previewState,
  };
};
